import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MAT_DIALOG_DATA, MatDialogClose } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';

import { PharmacologicalItemMetaData, PrescriptionItem, PrescriptionTypes, ServiceMetadata } from '@lib/core/models/prescription.interface';
import { InstrumentalDiagnostics, LaboratoryService, PharmacologicalTreatment, Protocol, RelatedSpecialist } from '@lib/core/models/protocol.interface';
import { ProtocolService } from '@lib/core/services/protocol.service';

import { WarningMessageComponent } from '../../commons/warning-message/warning-message.component';
import { LoaderComponent } from '../../layout/loader/loader.component';

export interface ProtocolModalData {
  protocolId: string;
  prescriptions: {
    drugs: PrescriptionItem<PharmacologicalItemMetaData>[];
    relatedSpecialists: PrescriptionItem<ServiceMetadata>[];
    laboratoryServices: PrescriptionItem<ServiceMetadata>[];
    instrumentalDiagnostics: PrescriptionItem<ServiceMetadata>[];
    personalDoctors?: PrescriptionItem<ServiceMetadata>[];
  };
}

@Component({
  selector: 'shared-protocol-modal',
  imports: [MatDialogClose, MatIcon, LoaderComponent, WarningMessageComponent, NgClass],
  template: `
    <div class="flex max-h-[90vh] flex-col gap-2 p-6">
      <div class="flex px-2 py-4">
        <h3 class="flex-1">{{ 'Деталі протоколу' }}</h3>
        <button mat-dialog-close class="self-end">
          <mat-icon aria-hidden="false" aria-label="Close icon" fontIcon="close"></mat-icon>
        </button>
      </div>
      <div class="relative flex min-h-[100px] flex-col gap-3 overflow-y-auto">
        @if (loading()) {
          <div class="absolute inset-0 z-50 flex items-center justify-center bg-gray-5 backdrop-blur-sm">
            <shared-loader></shared-loader>
          </div>
        }
        @if (protocol()) {
          <div class="flex flex-col gap-6">
            <div class="flex flex-col gap-1">
              <span class="text-gray-2">{{ 'Назва протоколу' }}</span>
              <span class="text-xl font-semibold">{{ protocol()!.name }}</span>
            </div>

            @if (protocol()!.pharmacologicalTreatments?.length) {
              <div>
                <span class="text-gray-2">{{ 'Медикаментозне лікування' }}</span>
                <div class="flex flex-col gap-3">
                  @for (drug of protocol()!.pharmacologicalTreatments; track drug.code) {
                    <div
                      class="flex items-center justify-between rounded-md bg-gray-4 p-2.5"
                      [ngClass]="{ 'bg-green-mid': isProtocolPrescriptionAdded(drug, prescriptionTypes.Drugs) }">
                      {{ drug.code }}
                      @if (isProtocolPrescriptionAdded(drug, prescriptionTypes.Drugs)) {
                        <span class="flex items-center gap-1 font-thin text-green-success">
                          <mat-icon>check</mat-icon>
                          {{ 'Додано' }}
                        </span>
                      }
                    </div>
                  }
                </div>
              </div>
            }

            @if (protocol()!.laboratoryServices?.length) {
              <div>
                <span class="text-gray-2">{{ 'Лабораторні послуги' }}</span>
                <div class="flex flex-col gap-3">
                  @for (service of protocol()!.laboratoryServices; track service.code) {
                    <div
                      class="flex items-center justify-between rounded-md bg-gray-4 p-2.5"
                      [ngClass]="{ 'bg-green-mid': isProtocolPrescriptionAdded(service, prescriptionTypes.LaboratoryService) }">
                      {{ service.code }} {{ service.nameUa }}
                      @if (isProtocolPrescriptionAdded(service, prescriptionTypes.LaboratoryService)) {
                        <span class="flex items-center gap-1 font-thin text-green-success">
                          <mat-icon>check</mat-icon>
                          {{ 'Додано' }}
                        </span>
                      }
                    </div>
                  }
                </div>
              </div>
            }

            @if (protocol()!.relatedSpecialists?.length) {
              <div>
                <span class="text-gray-2">{{ 'Суміжні спеціалісти' }}</span>
                <div class="flex flex-col gap-3">
                  @for (relatedSpecialist of protocol()!.relatedSpecialists; track relatedSpecialist.code) {
                    <div
                      class="flex items-center justify-between rounded-md bg-gray-4 p-2.5"
                      [ngClass]="{ 'bg-green-mid': isProtocolPrescriptionAdded(relatedSpecialist, prescriptionTypes.RelatedSpecialists) }">
                      {{ relatedSpecialist.code }} {{ relatedSpecialist.nameUa }}
                      @if (isProtocolPrescriptionAdded(relatedSpecialist, prescriptionTypes.RelatedSpecialists)) {
                        <span class="flex items-center gap-1 font-thin text-green-success">
                          <mat-icon>check</mat-icon>
                          {{ 'Додано' }}
                        </span>
                      }
                    </div>
                  }
                </div>
              </div>
            }

            @if (protocol()!.instrumentalDiagnoses?.length) {
              <div>
                <span class="text-gray-2">{{ 'Інструментальна діагностика' }}</span>
                <div class="flex flex-col gap-3">
                  @for (diagnostic of protocol()!.instrumentalDiagnoses; track diagnostic.code) {
                    <div
                      class="flex items-center justify-between rounded-md bg-gray-4 p-2.5"
                      [ngClass]="{ 'bg-green-mid': isProtocolPrescriptionAdded(diagnostic, prescriptionTypes.InstrumentalDiagnostic) }">
                      {{ diagnostic.code }} {{ diagnostic.nameUa }}
                      @if (isProtocolPrescriptionAdded(diagnostic, prescriptionTypes.InstrumentalDiagnostic)) {
                        <span class="flex items-center gap-1 font-thin text-green-success">
                          <mat-icon>check</mat-icon>
                          {{ 'Додано' }}
                        </span>
                      }
                    </div>
                  }
                </div>
              </div>
            }

            <div>
              <span class="text-gray-2">{{ 'Диференційні діагнози' }}</span>
              <div class="flex gap-4 py-3">
                @for (differentialDiagnosis of protocol()!.differentialDiagnoses; track differentialDiagnosis.code) {
                  <div class="border-1 rounded-full border-blue-secondary bg-blue-primary px-1 text-white">
                    {{ differentialDiagnosis.code }}
                  </div>
                }
              </div>
            </div>

            <div class="flex items-center justify-between">
              <span class="text-gray-2">{{ 'Наявність стаціонару' }}</span>
              <div class="border-1 inline-block min-w-[60px] rounded-full border-blue-secondary bg-blue-primary px-1 text-center text-white">
                {{ protocol()!.inpatientTreatment ? 'так' : 'ні' }}
              </div>
            </div>

            @if (protocol()!.nonPharmacologicalTreatments?.length) {
              <div>
                <span class="text-gray-2">{{ 'Немедикаментозне лікування' }}</span>
                <div class="flex flex-col gap-3">
                  @for (nonPharmacologicalTreatment of protocol()!.nonPharmacologicalTreatments; track nonPharmacologicalTreatment.code) {
                    <div class="flex items-center justify-between rounded-md bg-gray-4 p-2.5">{{ nonPharmacologicalTreatment.code }} {{ nonPharmacologicalTreatment.nameUa }}</div>
                  }
                </div>
              </div>
            }

            @if (protocol()!.surgicalEndoscopicTreatments?.length) {
              <div>
                <span class="text-gray-2">{{ 'Ендоскопічні дослідження' }}</span>
                <div class="flex flex-col gap-3">
                  @for (surgicalTreatment of protocol()!.surgicalEndoscopicTreatments; track surgicalTreatment.code) {
                    <div class="flex items-center justify-between rounded-md bg-gray-4 p-2.5">{{ surgicalTreatment.code }} {{ surgicalTreatment.nameUa }}</div>
                  }
                </div>
              </div>
            }

            @if (protocol()!.rehabilitation?.length) {
              <div>
                <span class="text-gray-2">{{ 'Реабілітація' }}</span>
                <div class="flex flex-col gap-3">
                  @for (rehab of protocol()!.rehabilitation; track rehab.code) {
                    <div class="flex items-center justify-between rounded-md bg-gray-4 p-2.5">{{ rehab.code }} {{ rehab.nameUa }}</div>
                  }
                </div>
              </div>
            }

            @if (protocol()!.preventions?.length) {
              <div>
                <span class="text-gray-2">{{ 'Превенція' }}</span>
                <div class="flex flex-col gap-3">
                  @for (prevention of protocol()!.preventions; track prevention) {
                    <div class="flex items-center justify-between rounded-md bg-gray-4 p-2.5">
                      {{ prevention }}
                    </div>
                  }
                </div>
              </div>
            }

            @if (protocol()!.referenceInformation?.length) {
              <div>
                <span class="text-gray-2">{{ 'Довідкова інформація' }}</span>
                <div class="flex flex-col gap-3">
                  @for (ref of protocol()!.referenceInformation; track ref) {
                    <div class="flex items-center justify-between rounded-md bg-gray-4 p-2.5">
                      {{ ref }}
                    </div>
                  }
                </div>
              </div>
            }

            @if (protocol()!.generalRecommendations?.length) {
              <div>
                <span class="text-gray-2">{{ 'Рекомендації' }}</span>
                <div class="flex flex-col gap-3">
                  @for (recommendation of protocol()!.generalRecommendations; track recommendation.code) {
                    <div class="flex items-center justify-between rounded-md bg-gray-4 p-2.5">
                      {{ recommendation.code }}
                    </div>
                  }
                </div>
              </div>
            }
          </div>
        } @else {
          <shared-warning-message [text]="'Не вдалося отримати дані протоколу'"></shared-warning-message>
        }
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProtocolModalComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly protocolService = inject(ProtocolService);
  protected readonly data: ProtocolModalData = inject(MAT_DIALOG_DATA);

  protected readonly prescriptionTypes = PrescriptionTypes;

  protected readonly loading = signal<boolean>(true);
  protected readonly protocol = signal<Protocol | null>(null);

  protected readonly prescriptions = computed(() => this.data.prescriptions);

  ngOnInit() {
    this.getProtocol();
  }

  private getProtocol() {
    this.protocolService
      .getProtocolById(this.data.protocolId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: protocol => this.protocol.set(protocol),
        error: () => this.loading.set(false),
        complete: () => this.loading.set(false),
      });
  }

  isProtocolPrescriptionAdded(prescription: InstrumentalDiagnostics | RelatedSpecialist | LaboratoryService | PharmacologicalTreatment, type: PrescriptionTypes) {
    switch (type) {
      case PrescriptionTypes.RelatedSpecialists: {
        return !!this.prescriptions().relatedSpecialists.find(item => item.metadata.code === (prescription as RelatedSpecialist).code);
      }
      case PrescriptionTypes.LaboratoryService: {
        return !!this.prescriptions().laboratoryServices.find(item => item.metadata.code === (prescription as LaboratoryService).code);
      }
      case PrescriptionTypes.InstrumentalDiagnostic: {
        return !!this.prescriptions().instrumentalDiagnostics.find(item => item.metadata.code === (prescription as InstrumentalDiagnostics).code);
      }
      case PrescriptionTypes.Drugs: {
        return !!this.prescriptions().drugs.find(item => item.metadata.inn === (prescription as PharmacologicalTreatment).protocolIdentifier);
      }
      default:
        return false;
    }
  }
}
